import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Table, Button, Grid } from 'antd';
import { useFetchAuditTrailLogs } from './redux/hooks';
import { useFetchManageOutsOrders } from './redux/fetchManageOutsOrders';
import { CustomerTitle } from '../OgmSubs';
import moment from 'moment';
import { useExportManageOutsHistorySearch } from './redux/hooks';
import './ManageOutsAuditTrailOrders.less';

export default function ManageOutsAuditTrailOrders(props) {
  const { selectedOrder, setSelectedOrder, searchFilters } = props;
  // const [sorting, setSorting] = useState({ sortedBy: null, sort: null });
  // const safeSetSorting = s => setSorting(s || { sortedBy: null, sort: null });
  const DEFAULT_PAGE_SIZE = 5;
  const EMPTY_SORT = { sortedBy: null, sort: null };

  const [sorting, setSorting] = useState(EMPTY_SORT);
  const safeSetSorting = s => setSorting(s || EMPTY_SORT);
  const skipNextSortEffectRef = useRef(false);

  const [localSelectedOrder, setLocalSelectedOrder] = useState(null);
  const activeSelectedOrder = selectedOrder || localSelectedOrder;
  const { exportHistorySearch, exportHistorySearchPending } = useExportManageOutsHistorySearch();

  const { ordersData, ordersTotal, fetchOrdersPending } = useSelector(state => ({
    ordersData: state.manageOutsAuditTrail.ordersData,
    ordersTotal: state.manageOutsAuditTrail.ordersTotal,
    fetchOrdersPending: state.manageOutsAuditTrail.fetchOrdersPending,
  }));

  const { fetchAuditTrailLogs } = useFetchAuditTrailLogs();
  const { fetchManageOutsOrders } = useFetchManageOutsOrders();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const colorMap = {
    OPEN: '#fa8c16',
    CANCEL: '#d4380d',
    SHIPPED: '#52c41a',
    ROUTED: '#1890ff',
    'ERRORS EXIST': '#8B4513',
    'SEND TO ROUTING': '#fadb14',
  };

  // const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
   const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });

  const didMountRef = useRef(false);
  // useEffect(() => {
  //   if (!didMountRef.current) {
  //     didMountRef.current = true;
  //     return;
  //   }
  //   loadOrders({ pageIndex: 1, pageSize: pagination.pageSize, sortedBy: sorting.sortedBy, sort: sorting.sort });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [sorting]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (skipNextSortEffectRef.current) {
      skipNextSortEffectRef.current = false;
      return;
    }
    loadOrders({ pageIndex: 1, pageSize: pagination.pageSize, sortedBy: sorting.sortedBy, sort: sorting.sort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, total: ordersTotal || 0 }));
  }, [ordersTotal]);

  useEffect(() => {
    if (selectedOrder) return;
    if (!localSelectedOrder) return;

    const selNum = String(localSelectedOrder.orderNumber || localSelectedOrder.orderNo || '');
    const present = (ordersData || []).some(r => String(r.orderNumber || r.orderNo || '') === selNum);

    if (!present) {
      setLocalSelectedOrder(null);
    }
  }, [ordersData, localSelectedOrder, selectedOrder]);

  useEffect(() => {
    const hasSearch = searchFilters && Object.keys(searchFilters).length > 0;
    if (!hasSearch) return;

    setLocalSelectedOrder(null);
    setPagination({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });

    skipNextSortEffectRef.current = true;
    safeSetSorting(EMPTY_SORT);

    loadOrders({ pageIndex: 1, pageSize: DEFAULT_PAGE_SIZE, resetSort: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilters]);

  const formatDateArg = (d) => {
    if (!d) return '';
    const s = String(d);
    return /^\d{8}$/.test(s) ? s : moment(d).format('YYYYMMDD');
  };

  const exportOrders = () => {
    const source = activeSelectedOrder || searchFilters || {};
    const payload = {
      customerShipTo: source.customerShipTo || source.customerNumber || '',
      deliveryDateFrom: formatDateArg(source.deliveryDate || source.deliveryDateFrom),
      deliveryDateTo: formatDateArg(source.deliveryDate || source.deliveryDateTo),
      itemNumber: source.originalItem || source.itemNumber || '',
      orderNumber: source.orderNumber || source.orderNo || '',
      site: source.site || source.opco || '',
    };
    // normalize Ant Design sort values if present
    if (payload.sort === 'ascend') payload.sort = 'asc';
    else if (payload.sort === 'descend') payload.sort = 'desc';
    exportHistorySearch(payload).catch(() => { });
  };
  const loadOrders = (opts = {}) => {
    setLocalSelectedOrder(null);
    const pageIndex = Number(opts.pageIndex) || pagination.current || 1;
    const pageSize = Number(opts.pageSize) || pagination.pageSize || 5;
    const search = searchFilters || {};

    // const merged = {
    //   ...search,
    //   pageIndex,
    //   pageSize,
    //   ...(opts.sortedBy ? { sortedBy: opts.sortedBy } : (sorting && sorting.sortedBy ? { sortedBy: sorting.sortedBy } : {})),
    //   ...(opts.sort ? { sort: opts.sort } : (sorting && sorting.sort ? { sort: sorting.sort } : {})),
    // };

    const resolvedSortedBy = opts.resetSort
      ? null
      : (Object.prototype.hasOwnProperty.call(opts, 'sortedBy') ? opts.sortedBy : (sorting && sorting.sortedBy));
    const resolvedSort = opts.resetSort
      ? null
      : (Object.prototype.hasOwnProperty.call(opts, 'sort') ? opts.sort : (sorting && sorting.sort));

    const merged = {
      ...search,
      pageIndex,
      pageSize,
      ...(resolvedSortedBy ? { sortedBy: resolvedSortedBy } : {}),
      ...(resolvedSort ? { sort: resolvedSort } : {}),
    };

    const payload = Object.keys(merged).reduce((acc, k) => {
      const v = merged[k];
      if (v === undefined || v === '') return acc;
      if (k === 'sort') {
        acc[k] = v === 'ascend' ? 'asc' : v === 'descend' ? 'desc' : v;
      } else {
        acc[k] = v;
      }
      return acc;
    }, {});

    // return fetchManageOutsOrders(payload).then(resp => {
    //   if (resp) {
    //     setPagination(prev => ({
    //       ...prev,
    //       total: Number(resp.total) || 0,
    //       current: Number(resp.pageIndex) || pageIndex,
    //       pageSize: Number(resp.pageSize) || pageSize,
    //     }));
    //   }
    //   return resp;
    // });
    return fetchManageOutsOrders(payload).then(resp => {
      if (resp) {
        setPagination(prev => ({
          ...prev,
          total: Number(resp.total) || 0,
          current: Number(resp.pageIndex) || pageIndex,
          pageSize: Number(resp.pageSize) || pageSize,
        }));
        if (Array.isArray(resp.rows) && resp.rows.length === 1 && Number(resp.total) === 1) {
          fetchOrderList(resp.rows[0]);
        }
      }
      return resp;
    });

  };

  const onTableChange = (paginationObj, filters, sorter) => {
    const p = (paginationObj && paginationObj.current) || 1;
    const ps = (paginationObj && paginationObj.pageSize) || pagination.pageSize;

    const field = sorter && (sorter.field || sorter.columnKey);
    const order = (sorter && Object.prototype.hasOwnProperty.call(sorter, 'order')) ? sorter.order : null;

    // Detect an actual sort action (user changed sort column or order)
    const isSortChange = field && (field !== (sorting && sorting.sortedBy) || order !== (sorting && sorting.sort));

    if (isSortChange) {
      // Update sort state only; the useEffect([sorting]) will call loadOrders exactly once.
      safeSetSorting({ sortedBy: field, sort: order });
      //setPagination(prev => ({ ...prev, pageIndex: 1 }));
      setPagination(prev => ({ ...prev, current: 1 }));
      return;
    }

    // Otherwise treat as pagination change — load once with current sorting
    const currPage = ps !== pagination.pageSize ? 1 : p;
    //setPagination(prev => ({ ...prev, pageIndex: currPage, pageSize: ps }));
    setPagination(prev => ({ ...prev, current: currPage, pageSize: ps }));
    loadOrders({ pageIndex: currPage, pageSize: ps });
  };

  const onPaginationChange = (page, pageSize) => {
    // if pageSize changed, reset to page 1
    const currPage = pageSize !== pagination.pageSize ? 1 : page;
    //setPagination(prev => ({ ...prev, pageIndex: currPage, pageSize }));
    setPagination(prev => ({ ...prev, current: currPage, pageSize }));
    // loadOrders will include current `sorting` automatically
    loadOrders({ pageIndex: currPage, pageSize });
  };

  const fetchOrderList = record => {
    const snapshot = { ...record };
    const sf = searchFilters || {};
    const itemNumber = snapshot.originalItem || snapshot.itemNumber || snapshot.originalItemNumber || snapshot.item || snapshot.supc || sf.itemNumber || sf.originalItem || undefined;
    const site = snapshot.site || snapshot.opco || sf.site || '';
    const orderNumber = snapshot.orderNumber || snapshot.orderNo || sf.orderNumber || sf.orderNo || '';
    const conceptId = snapshot.conceptId || sf.conceptId || '';
    const customerShipTo = snapshot.customerShipTo || snapshot.customerNumber || sf.customerShipTo || '';
    const deliveryDate = snapshot.deliveryDate || sf.deliveryDate || sf.deliveryDateFrom || '';
    if (typeof setSelectedOrder === 'function') {
      setSelectedOrder({ ...snapshot, itemNumber });
    }

    setLocalSelectedOrder({ ...snapshot, itemNumber });

    const params = {
      site,
      orderNumber,
      customerShipTo,
      conceptId,
      deliveryDateFrom: deliveryDate,
      deliveryDateTo: deliveryDate,
      itemNumber,
      // pageIndex: 1,
      // pageSize: pagination.pageSize,
      pageIndex: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    const payload = Object.keys(params).reduce((acc, k) => {
      const v = params[k];
      if (v === undefined || v === '') return acc;
      acc[k] = v;
      return acc;
    }, {});

    // fetchAuditTrailLogs(payload).then(resp => {
    //   if (resp) {
    //     setPagination(prev => ({
    //       ...prev,
    //       total: resp.total || 0,
    //       current: resp.pageIndex || 1,
    //       pageSize: resp.pageSize || prev.pageSize,
    //     }));
    //   }
    // }).catch(() => { });
        fetchAuditTrailLogs(payload).catch(() => { });
  };

  const actionColumn = {
    title: '',
    dataIndex: 'action',
    render: (value, record) => (
      <Button type="primary" onClick={() => fetchOrderList(record)} size="small">
        Select
      </Button>
    ),
  };

  const renderIdentifierValue = (record) => {
  const type = searchFilters && searchFilters.identifierType;

  if (type === 'conceptId') {
    return record.conceptId || '—';
  }

  if (type === 'customerShipTo') {
    return record.customerShipTo || record.customerNumber || '—';
  }

  return record.conceptId || record.customerShipTo || record.customerNumber || '—';
};

  const columns = [
    {
      title: <CustomerTitle setSorting={safeSetSorting} sorting={sorting} dataIndex="site" title="Site" />,
      dataIndex: 'site',
      width: 180,
      blankWidth: 180,
      sortOrder: sorting.sortedBy === 'site' ? sorting.sort : null,
    },
    {
      title: <CustomerTitle setSorting={safeSetSorting} sorting={sorting} dataIndex="orderNumber" title="Order #" />,
      dataIndex: 'orderNumber',
      width: 180,
      blankWidth: 180,
      sortOrder: sorting.sortedBy === 'orderNumber' ? sorting.sort : null,
    },
    {
      title: <CustomerTitle setSorting={safeSetSorting} sorting={sorting} dataIndex="customerShipTo" title="Customer # / Ship To" />,
      dataIndex: 'customerShipTo',
      width: 200,
      blankWidth: 200,
      sortOrder: sorting.sortedBy === 'customerShipTo' ? sorting.sort : null,
      // render: (_, record) =>
      //   record.conceptId || record.customerShipTo || record.customerNumber || '—',
      render: (_, record) => renderIdentifierValue(record),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 200,
      blankWidth: 200,
      render: (val) => {
        const text = val ? String(val) : '';
        const key = text.toUpperCase().trim();
        const color = colorMap[key] || (key.includes('ROUTING') ? colorMap['SEND TO ROUTING'] : '#6c757d');
        return <span style={{ color, fontWeight: 600 }}>{text || '—'}</span>;
      },
    },
    {
      title: <CustomerTitle setSorting={safeSetSorting} sorting={sorting} dataIndex="total" title="Total" />,
      dataIndex: 'orderTotal',
      width: 180,
      blankWidth: 180,
      sortOrder: sorting.sortedBy === 'total' ? sorting.sort : null,
    },
    {
      title: <CustomerTitle setSorting={safeSetSorting} sorting={sorting} dataIndex="deliveryDate" title="Delivery Date" />,
      dataIndex: 'deliveryDate',
      width: 180,
      blankWidth: 180,
      render: d => (d ? moment(d).format('MM/DD/YYYY') : ''),
      sortOrder: sorting.sortedBy === 'deliveryDate' ? sorting.sort : null,
    },
  ];
  columns.splice(columns.length, 0, actionColumn);

  return (
    <div className="manage-outs-orders">

      <div className="chart-title flex justify-space-between">
        <p className="font-size-16-bold" style={{ margin: 0 }}>Orders</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {!isMobile && (
            <div className="export-button">
              <Button
                type="link"
                onClick={exportOrders}
                disabled={!ordersData || ordersData.length === 0 || exportHistorySearchPending}
              >
                Export
              </Button>
            </div>
          )}

          <span className="results-count">
            {ordersTotal ? `${ordersTotal} result${ordersTotal > 1 ? 's' : ''}` : '0 results'}
          </span>
        </div>
      </div>

      <div className="manage-outs-table-wrap">
        <Table
          rowKey={record => `${record.id || record.orderNumber}`}
          onChange={onTableChange}
          rowClassName={record =>
            (activeSelectedOrder && String(activeSelectedOrder.orderNumber) === String(record.orderNumber))
              ? 'order-row-selected'
              : ''
          }
          className="dashboard-order-report-table orders-table"
          loading={fetchOrdersPending}
          columns={columns}
          dataSource={ordersData}
          // pagination={{
          //   current: pagination.current,
          //   pageSize: pagination.pageSize,
          //   total: pagination.total,
          //   showSizeChanger: true,
          //   pageSizeOptions: ['5', '10', '20', '50', '100'],
          // }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: ordersTotal || 0,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50', '100'],
          }}
        />
      </div>
    </div>
  );
}

ManageOutsAuditTrailOrders.propTypes = {};
ManageOutsAuditTrailOrders.defaultProps = {};


